/* eslint-disable camelcase */
// Requested-order classification. This catalogue replaces SPECIAL_MARKETPLACES
// for this screen only; it does not change shop or purchase configuration.
export const SPECIAL_STORE_DOMAINS = [
  'nike.com', 'sephora.es', 'disneystore.es', 'asics.com', 'footlocker.es',
  'newbalance.es', 'lego.com', 'gucci.com', 'store.nintendo.es', 'flashstore.es',
  'weekendmaxmara.com', 'outlet.asics.com', 'tiffany.es', 'hermes.com',
  'apple.com', 'louisvuitton.com', 'miumiu.com', 'yslbeauty.es', 'ysl.com',
  'vinted.es', 'wallapop.com', 'sklum.com', 'ebay.es', 'jdsports.es'
] as const

export const SPECIAL_STORE_IDS = [114, 129, 134, 144, 145, 172, 191, 223, 2063, 2397, 3347, 9658, 13264, 24271]

type Store = { id?: number, original_url?: string | null }
export type RequestedStoreOrder = {
  marketplace_id?: Store | number | null
  custom_marketplace_id?: number | null
  items?: { deleted?: boolean | null, url?: string | null, custom_order_site?: string | null }[] | null
}

export function purchaseHostname (value?: string | null): string | null {
  if (!value || !value.trim() || (value.trim().startsWith('/') && !value.trim().startsWith('//'))) return null
  try {
    const raw = value.trim()
    const url = new URL(raw.startsWith('//') ? `https:${raw}` : /^[a-z][a-z0-9+.-]*:/i.test(raw) ? raw : `https://${raw}`)
    if (!['https:', 'http:'].includes(url.protocol) || url.username || url.password) return null
    return url.hostname.toLowerCase().replace(/\.$/, '').replace(/^www\./, '')
  } catch {
    return null
  }
}

function store (order: RequestedStoreOrder): Store {
  return typeof order.marketplace_id === 'object' && order.marketplace_id ? order.marketplace_id : { id: order.marketplace_id ?? undefined }
}

function purchaseHosts (order: RequestedStoreOrder): string[] {
  return Array.from(new Set([
    ...((order.items || []).filter(item => item.deleted !== true).flatMap(item => [item.url, item.custom_order_site])),
    store(order).original_url
  ].map(purchaseHostname).filter((host): host is string => host !== null)))
}

export function isSpecialRequestedOrder (order: RequestedStoreOrder): boolean {
  return SPECIAL_STORE_IDS.includes(store(order).id ?? -1) ||
    SPECIAL_STORE_IDS.includes(order.custom_marketplace_id ?? -1) ||
    purchaseHosts(order).some(host => SPECIAL_STORE_DOMAINS.some(domain => host === domain || host.endsWith(`.${domain}`)))
}

export function getPurchaseUrl (order: RequestedStoreOrder): string {
  const marketplace = store(order)
  const items = (order.items || []).filter(item => item.deleted !== true)
  // Show one retailer domain, never the SYNIVA catalogue/proxy address.
  const candidates = [
    ...(marketplace.id !== 0 ? [marketplace.original_url] : []),
    ...items.map(item => item.url),
    ...items.map(item => item.custom_order_site)
  ]
  for (const value of candidates) {
    const host = purchaseHostname(value)
    if (!host || !host.includes('.') || ['syniva.es', 'syniva.net'].some(domain => host === domain || host.endsWith(`.${domain}`))) continue
    // Preserve actual subdomains (www, es, outlet, store) without inventing one.
    const hasWww = /^(?:https?:\/\/|\/\/)?www\./i.test(value?.trim() || '')
    return `${hasWww ? 'www.' : ''}${host}`
  }
  return ''
}
